import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import Page from 'layout/access-management/page'
import PageTitle from 'layout/page-title'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import ConflictsTab from './conflicts-tab'
import { split } from 'lodash'
import useEmployeeDuties from 'api/hooks/use-employee-duties'
import EmptyPage from 'layout/access-management/empty-page'
import RolesTab from './roles-tab'
import PermissionsTab from './permissions-tab'
import Stats from './stats'
import AuditLogTab from './audit-log-tab'
import BusinessRulesTab from './BusinessRulesTab'
import UserBadges from './UserBadges'
import DataAccessTab from './DataAccessTab'
import Tabs from 'duties/shared/tabs'
import { useUpdateEmployee } from 'api/hooks/use-employees'
import { useQueryClient } from 'react-query'
import AddPermission from './AddPermission'
import ActionsDropdown from './ActionsDropdown'
import CompareUsers from './CompareUsers'

const VALID_TABS = ['conflicts', 'roles', 'permissions', 'data-access', 'business-rules', 'audit-log']

function Duty() {
  const path = window.location.pathname
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState(
    VALID_TABS.includes(initialTab) ? initialTab : 'conflicts'
  )
  const dutyId = split(path, '/')[2]
  const [activeKey, setActiveKey] = useState('')
  const [modalShow, setModalShow] = useState(false)
  const [compareShow, setCompareShow] = useState(false)
  const [isSystem, setIsSystem] = useState(false)

  const queryClient = useQueryClient()
  const { mutate: mutateEmployee } = useUpdateEmployee({
    options: {
      onSuccess: (_data, variables) => {
        if (variables?.params?.id) {
          queryClient.invalidateQueries([
            'employee',
            { id: variables.params.id }
          ])
          queryClient.invalidateQueries([
            'employees-duties',
            { id: variables.params.id }
          ])

          // When system user flag changes, refresh SOD-related views
          if (variables?.params?.is_system !== undefined) {
            queryClient.invalidateQueries('sod_counts')
            queryClient.invalidateQueries('duty_employees')
            queryClient.invalidateQueries('duty_roles')
            queryClient.invalidateQueries('conflicts')
            queryClient.invalidateQueries('permissions')
            queryClient.invalidateQueries('role_sod_counts')
            queryClient.invalidateQueries('employee_sod_counts')
          }
        }
      }
    }
  })

  const params = {
    id: dutyId
  }

  const {
    isSuccess,
    isLoading,
    isError,
    data: employee
  } = useEmployeeDuties({ params })
  const crumbs = [
    { name: 'Access Management', url: 'duties' },
    { name: 'Users', url: 'users' },
    { name: isSuccess ? employee.external_id : '', url: '' }
  ]

  const handleToggleSystemUser = (newValue) => {
    setIsSystem(newValue)
    mutateEmployee({ params: { id: dutyId, is_system: newValue } })
  }

  useEffect(() => {
    if (isSuccess) {
      setIsSystem(employee.is_system)
    }
  }, [isSuccess, employee?.is_system])

  if (isLoading) {
    return <EmptyPage />
  }

  return (
    <Page title={'User'}>
      <PageTitle breadcrumbs={crumbs} />
      <div className="d-flex justify-content-between align-items-center mt-4">
        <h4>
          {employee.external_id}
          <span className="small">
            <UserBadges user={employee} />
          </span>
        </h4>
        <div>
          {isSuccess && (
            <ActionsDropdown
              employee={employee}
              isSystem={isSystem}
              onToggleSystemUser={handleToggleSystemUser}
              onAddPermission={() => setModalShow(true)}
              onCompareUser={() => setCompareShow(true)}
            />
          )}
          {isSuccess && (
            <AddPermission
              employee={employee}
              show={modalShow}
              setShow={setModalShow}
            />
          )}
          {isSuccess && (
            <CompareUsers
              employee={employee}
              employeeId={employee.id}
              show={compareShow}
              setShow={setCompareShow}
            />
          )}
        </div>
      </div>

      <div className="mb-4">
        {isSuccess && <Stats employee={employee} setActiveTab={setActiveTab} />}
      </div>

      <Row className="mt-4">
        <Col sm={12}>
          <div className="d-flex mb-3">
            <Tabs
              tabs={[
                { name: 'Conflicts', value: 'conflicts' },
                { name: 'Roles', value: 'roles' },
                { name: 'Permissions', value: 'permissions' },
                { name: 'Data Access', value: 'data-access' },
                { name: 'Business Rules', value: 'business-rules' },
                { name: 'Audit Log', value: 'audit-log' }
              ]}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>
        </Col>
      </Row>

      {activeTab === 'conflicts' && (
        <ConflictsTab
          employee={employee}
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          isSuccess={isSuccess}
        />
      )}
      {activeTab === 'roles' && (
        <RolesTab employee={employee} isError={isError} />
      )}
      {activeTab === 'permissions' && (
        <PermissionsTab
          employeeId={employee.id}
          categories={employee.functional_categories}
          isError={isError}
        />
      )}
      {activeTab === 'audit-log' && <AuditLogTab employeeId={employee.id} />}
      {activeTab === 'business-rules' && (
        <BusinessRulesTab employee={employee} />
      )}
      {activeTab === 'data-access' && (
        <DataAccessTab employeeId={employee.id} />
      )}
    </Page>
  )
}

export default Duty
